import BigNumber from 'bignumber.js';
import { startOfDay } from 'date-fns';
import { z } from 'zod';
import { Company, FiscalRegime } from '~/types/company';
import { IdentityType } from '~/types/company-identity';
import { InvoicingSettings, PaymentSettings, StampSettings } from '~/types/company-settings';
import { Counterpart } from '~/types/counterparts';
import { CountryIsoCode } from '~/types/country';
import { DocumentType, EInvoiceType, WithholdingTaxType } from '~/types/document';
import {
  DiscountType,
  DocumentBuilderFlowForm,
  DocumentBuilderForm,
  DocumentBuilderLine,
  DocumentBuilderLineDiscount,
  DocumentBuilderLineVAT,
  RelatedDocumentType,
  SocialSecurityForm,
  TaxableMode,
  TransportInformations,
  WithholdingTaxForm,
} from '~/types/document-builder';
import { FlowExtendedPaymentStatus, FlowPaymentStatus, PaymentMethod } from '~/types/flow';
import { EsigibilitaIva, naturaValues, StampType, TipoCassa } from '~/types/invoicing';
import { isDefined } from '~/types/typeGuards';
import { calculatePresetDate } from '~/utils/flow';
import { defaultEInvoiceType, isForeignSelfInvoiceType } from '~/utils/invoicing';
import {
  calculateCompetencies,
  calculateVatItemsTotals,
  filterContributionBaseItems,
  findMaxVat,
  lineGrossTotals,
  lineNetTotals,
} from '~/utils/invoicing/items';
import { withholdingTaxable } from '~/utils/invoicing/withholding';
import {
  defaultZero,
  emptyToNull,
  isValidAmount,
  isValidDiscount,
  isValidManagementDataNumber,
  isValidPrice,
  isValidQuantity,
  isValidTaxableAmount,
  refineBasicLatin,
  refineLatin1,
  refineRequired,
  validateDDTPrices,
  validateDestinationCodeLength,
} from '~/utils/validation';
import { defaultDestinationCode, isTheCurrentActiveCompany } from './counterparts';
import { isPositive } from './numberInputs';
import { latin1Schema, MAX_ADMIN_REFERENCE_LENGTH } from './parsing';

export const MAX_DOCUMENT_NUMBER_LENGTH = 20;

const counterpartSchema = z.object({
  companyName: z.string().nullable().pipe(z.string().trim().min(1).max(80).superRefine(refineLatin1)),
  country: z.string().length(2),
  postalCode: z.string().nullable().pipe(z.string().trim().min(1)),
  destinationCode: z.string().nullable(),
  identityType: z.nativeEnum(IdentityType).nullable(),
  taxNumber: z.string().nullable(),
  vatNumber: z.string().nullable(),
});

const flowSchema = z.object({
  amount: z.string().transform(defaultZero).transform(isValidAmount).pipe(z.number().nonnegative()),
  expectedPaymentDate: z.date(),
  notes: z.string().trim().nullable(),
  paymentStatus: z.nativeEnum(FlowPaymentStatus),
});

const managementDataNumberSchema: z.Schema<string | null> = emptyToNull(
  z.string().transform(isValidManagementDataNumber),
);
const managementDataSchema = z.object({
  dataType: z.string(),
  text: z.string().nullable(),
  date: z.date().nullable(),
  number: managementDataNumberSchema,
});

const articleCodeSchema = z.object({
  type: z.string().trim(),
  value: z.string().trim().min(1).superRefine(refineBasicLatin),
});

const discountSchema: z.Schema<{ value: string; type: DiscountType }> = z
  .object({
    value: z.string(),
    type: z.nativeEnum(DiscountType),
  })
  .transform((obj, ctx) => {
    const isAmount = obj.type === DiscountType.Amount;
    const validatedValue = isValidDiscount(isAmount)(obj.value, ctx);

    return {
      type: obj.type,
      value: validatedValue,
    };
  });

const priceSchema: z.Schema<string> = z.string().transform(isValidPrice);
const quantitySchema: z.Schema<string> = z.string().trim().transform(isValidQuantity);

const getItemSchema = (type?: DocumentType) =>
  z
    .object({
      code: z.string().trim().superRefine(refineBasicLatin),
      articleCodes: z.array(articleCodeSchema).default([]),
      description: z.string().trim().min(1).superRefine(refineLatin1),
      discount: discountSchema,
      quantity: quantitySchema,
      netPrice: type === DocumentType.DeliveryNote ? z.string() : priceSchema,
      grossPrice: type === DocumentType.DeliveryNote ? z.string() : priceSchema,
      vat: z.object({
        percentage: z.string(),
        taxExemption: z.enum(naturaValues).nullable(),
        lawReference: z.string().nullable(),
      }),
      extendedDescription: z.string().trim().max(1000),
      managementData: z.array(managementDataSchema),
      competencyStartDate: z.date().nullable(),
      competencyEndDate: z.date().nullable(),
      riferimentoAmministrazione: z.string().trim().max(MAX_ADMIN_REFERENCE_LENGTH).superRefine(refineBasicLatin),
      unit: z.string(),
    })
    .superRefine((item, ctx) => {
      if (item.competencyStartDate && item.competencyEndDate && item.competencyEndDate < item.competencyStartDate) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          params: { issue: 'competency_end_before_start' },
          path: ['competencyEndDate'],
        });
      }
    });

const paymentInfoSchema = z.object({
  accountId: z.string().nullable(),
  paymentMethod: z.nativeEnum(PaymentMethod),
  paymentAccount: z.string().nullable(),
  paymentStatus: z.nativeEnum(FlowExtendedPaymentStatus).nullish(),
});

export const relatedDocumentSchema = z.object({
  type: z.nativeEnum(RelatedDocumentType),
  date: z.date().nullable().pipe(z.date()),
  documentNumber: latin1Schema.pipe(z.string().min(1)),
  CIGCode: z.string().max(15),
  CUPCode: z.string().max(15),
  orderCode: z.string().max(100),
});

const percentageSchema = z
  .string()
  .trim()
  .transform(defaultZero)
  .transform(isValidAmount)
  .pipe(z.number().nonnegative().max(100));

const taxableAmountSchema: z.Schema<string> = z.string().transform(isValidTaxableAmount);

const withholdingSchema = z.object({
  amountPercentage: percentageSchema,
  percentage: percentageSchema,
  motivation: z.string(),
  taxableAmount: taxableAmountSchema,
  type: z.nativeEnum(WithholdingTaxType),
});

const socialSecuritySchema = z
  .object({
    amountPercentage: percentageSchema.pipe(z.coerce.string()),
    percentage: percentageSchema.pipe(z.coerce.string()),
    taxableAmount: taxableAmountSchema,
    type: z.nativeEnum(TipoCassa),
  })
  .nullable();

const transportInformationsSchema = z.object({
  destination: z.object({
    address: z.string().trim().min(5),
    city: z.string().trim().min(2),
    country: z.string().trim().length(2),
    postalCode: z.string().trim().min(1),
  }),
  transportReason: z.string().min(2),
  courier: z.string().min(1),
  packsNumber: z.string().trim().transform(isValidAmount).pipe(z.number().nonnegative()),
  weight: z.string().trim().transform(isValidAmount).pipe(z.number().nonnegative()),
});

export const getDocumentBuilderSchema = (activeCompany: Company) => {
  const baseSchema = z.object({
    counterpart: counterpartSchema.nullable().transform(refineRequired),
    currency: z.string().length(3),
    fiscalRegime: z.nativeEnum(FiscalRegime),
    date: z.date(),
    flows: z.array(flowSchema).min(1),
    isEInvoice: z.boolean(),
    items: z.array(getItemSchema()).min(1),
    motivation: z.string().trim().max(600).nullable(),
    notes: z.string().trim().nullable(),
    paymentInfo: paymentInfoSchema,
    relatedDocuments: z.array(relatedDocumentSchema),
    vatCollection: z.nativeEnum(EsigibilitaIva).nullable(),
    withholdingTax: z.array(withholdingSchema),
    socialSecurity: socialSecuritySchema,
    attachmentsSize: z
      .number()
      .max(3 * 1024 * 1024)
      .optional(),
    useGrossPrices: z.boolean().nullable(),
  });

  const typeSubtypeSchema = z.union([
    z.object({
      type: z
        .nativeEnum(DocumentType)
        .pipe(
          z.enum([
            DocumentType.Invoice,
            DocumentType.CreditNote,
            DocumentType.DebitNote,
            DocumentType.Parcel,
            DocumentType.SelfInvoice,
          ]),
        ),
      subtype: z.nativeEnum(EInvoiceType),
    }),
    z.object({
      type: z
        .nativeEnum(DocumentType)
        .pipe(z.enum([DocumentType.DeliveryNote, DocumentType.Bill, DocumentType.Other, DocumentType.Quote])),
      subtype: z.null(),
    }),
  ]);

  return z
    .intersection(baseSchema, typeSubtypeSchema)
    .refine((data) => isDefined(data.counterpart.taxNumber) || isDefined(data.counterpart.vatNumber), {
      params: { issue: 'tax_vat_number_required' },
      path: ['counterpart'],
    })
    .refine((data) => !(isForeignSelfInvoiceType(data.subtype) && data.counterpart.country === 'IT'), {
      params: { issue: 'self_invoice_country_foreign' },
      path: ['counterpart', 'country'],
    })
    .refine(
      (data) =>
        !(
          data.type === DocumentType.SelfInvoice &&
          (data.subtype === EInvoiceType.Fattura ||
            data.subtype === EInvoiceType.CommunicationOmittedIrregularInvoice) &&
          data.counterpart.country !== 'IT'
        ),
      {
        params: { issue: 'self_invoice_country_italy' },
        path: ['counterpart', 'country'],
      },
    )
    .superRefine(({ counterpart }, ctx) => {
      const { country } = counterpart;
      const identityType = counterpart.identityType ?? IdentityType.Company;
      const destinationCode =
        counterpart.destinationCode ??
        defaultDestinationCode(counterpart.destinationCode, country as CountryIsoCode, identityType);

      const issue = validateDestinationCodeLength(destinationCode, country as CountryIsoCode, identityType);
      if (issue) {
        ctx.addIssue({ ...issue, path: ['counterpart', 'destinationCode'] });
      }
    })
    .superRefine(({ fiscalRegime, items, socialSecurity, useGrossPrices, currency }, ctx) => {
      if (!socialSecurity) {
        return;
      }
      const taxableItems = filterContributionBaseItems(items, fiscalRegime);
      const linesTotals = useGrossPrices ? taxableItems.map(lineGrossTotals) : taxableItems.map(lineNetTotals);
      const vatItemsTotals = calculateVatItemsTotals(taxableItems, currency, useGrossPrices ?? false);
      const netTotalAmount = calculateCompetencies(linesTotals, useGrossPrices ?? false, vatItemsTotals);
      const taxableAmount = BigNumber(socialSecurity.taxableAmount);
      if (taxableAmount.isGreaterThan(netTotalAmount)) {
        ctx.addIssue({
          code: z.ZodIssueCode.too_big,
          origin: 'number',
          maximum: netTotalAmount.toNumber(),
          inclusive: true,
          path: ['socialSecurity', 'taxableAmount'],
        });
      }
    })
    .superRefine(({ fiscalRegime, items, socialSecurity, withholdingTax, useGrossPrices, currency }, ctx) => {
      const taxableItems = filterContributionBaseItems(items, fiscalRegime);
      const taxableItemsNonNegative = taxableItems.filter((i) => {
        // Exclude items with negative ammount like advanced payment to allow user to aexlude some lines
        return isPositive(i.netPrice) && isPositive(i.grossPrice);
      });
      const linesTotals = useGrossPrices
        ? taxableItemsNonNegative.map(lineGrossTotals)
        : taxableItemsNonNegative.map(lineNetTotals);
      const vatItemsTotals = calculateVatItemsTotals(taxableItemsNonNegative, currency, useGrossPrices ?? false);
      const netTotalAmount = calculateCompetencies(linesTotals, useGrossPrices ?? false, vatItemsTotals);
      const maxVatAmount = findMaxVat(taxableItems);
      const taxable = withholdingTaxable(netTotalAmount, socialSecurity, maxVatAmount);
      withholdingTax.forEach((item, index) => {
        const taxableAmount = BigNumber(item.taxableAmount);
        if (taxableAmount.isGreaterThan(taxable)) {
          ctx.addIssue({
            code: z.ZodIssueCode.too_big,
            origin: 'number',
            maximum: taxable.toNumber(),
            inclusive: true,
            path: ['withholdingTax', index, 'taxableAmount'],
          });
        }
      });
    })
    .refine(
      (data) =>
        !(
          data.subtype === EInvoiceType.AutofatturaPerAutoconsumo &&
          !isTheCurrentActiveCompany(data.counterpart as Counterpart, activeCompany)
        ),
      {
        params: { issue: 'self_invoice_counterpart_same_as_company' },
        path: ['counterpart'],
      },
    )
    .refine(
      (data) =>
        !(
          data.subtype === EInvoiceType.AutofatturaPerAutoconsumo &&
          !data.flows.every((flow) => flow.paymentStatus === FlowPaymentStatus.Paid)
        ),
      {
        params: { issue: 'self_invoice_flows_must_be_paid' },
        path: ['flows'],
      },
    );
};

export const deliveryNoteBuilderSchema = z
  .object({
    counterpart: counterpartSchema,
    currency: z.string().length(3),
    date: z.date(),
    includePrices: z.boolean().nullable(),
    useGrossPrices: z.boolean().nullable(),
    items: z.array(getItemSchema(DocumentType.DeliveryNote)).nonempty(),
    motivation: z.string().trim().nullable(),
    notes: z.string().trim().nullable(),
    relatedDocuments: z.array(relatedDocumentSchema),
    type: z.enum([DocumentType.DeliveryNote]),
    transportInformations: transportInformationsSchema,
  })
  .refine((data) => isDefined(data.counterpart.taxNumber) || isDefined(data.counterpart.vatNumber), {
    params: { issue: 'tax_vat_number_required' },
    path: ['counterpart'],
  })
  .superRefine(({ counterpart }, ctx) => {
    const { country } = counterpart;
    const identityType = counterpart.identityType ?? IdentityType.Company;
    const destinationCode =
      counterpart.destinationCode ??
      defaultDestinationCode(counterpart.destinationCode, country as CountryIsoCode, identityType);

    const issue = validateDestinationCodeLength(destinationCode, country as CountryIsoCode, identityType);
    if (issue) {
      ctx.addIssue({ ...issue, path: ['counterpart', 'destinationCode'] });
    }
  })
  .superRefine(validateDDTPrices);

export const finalizeFormSchema = z.object({
  number: z
    .string()
    .trim()
    .min(1)
    .max(MAX_DOCUMENT_NUMBER_LENGTH)
    .superRefine(refineBasicLatin)
    .refine((val) => /\d/g.test(val), {
      params: { issue: 'digit_required' },
    }),
});

export const finalizeFormWithSectionalSchema = z.object({
  sectionalId: z.string().nullable().pipe(z.string().min(1)),
  progressive: z.bigint().nullable().pipe(z.bigint().positive()),
  number: z
    .string()
    .trim()
    .min(1)
    .max(MAX_DOCUMENT_NUMBER_LENGTH)
    .refine((val) => /\d/g.test(val), {
      params: { issue: 'digit_required' },
    }),
});

export const relatedDocumentToDocumentType = (related: RelatedDocumentType) => {
  switch (related) {
    case RelatedDocumentType.Ddt:
      return [DocumentType.DeliveryNote];
    case RelatedDocumentType.FatturaCollegata:
      return [
        DocumentType.Invoice,
        DocumentType.CreditNote,
        DocumentType.SelfInvoice,
        DocumentType.Parcel,
        DocumentType.Bill,
        DocumentType.DebitNote,
      ];
    case RelatedDocumentType.Ricezione:
      return [DocumentType.Other];
    case RelatedDocumentType.Contratto:
      return [DocumentType.Other];
    case RelatedDocumentType.Convenzione:
      return [DocumentType.Other];
    case RelatedDocumentType.OrdineAcquisto:
      return [DocumentType.Quote];
    default:
      return [];
  }
};

export const createEmptyFlow = (
  documentDate: Date,
  payment: PaymentSettings,
  counterpart?: Counterpart | null,
): DocumentBuilderFlowForm => {
  const defaultPaymentDate = counterpart?.paymentDate ?? payment.paymentDate;
  const expectedPaymentDate = startOfDay(calculatePresetDate(documentDate, defaultPaymentDate));
  return {
    remoteId: null,
    amount: '0',
    expectedPaymentDate,
    notes: '',
    paymentStatus: FlowPaymentStatus.ToPay,
  };
};

export const defaultDiscount: DocumentBuilderLineDiscount = {
  type: DiscountType.Percentage,
  value: '0',
};
const createDefaultItem = (vat: DocumentBuilderLineVAT) => {
  return {
    code: '',
    articleCodes: [],
    description: '',
    extendedDescription: '',
    discount: defaultDiscount,
    quantity: '1',
    unit: '',
    netPrice: '0',
    grossPrice: '0',
    vat: {
      lawReference: vat.lawReference,
      percentage: vat.percentage,
      taxExemption: vat.taxExemption,
    },
    managementData: [],
    competencyStartDate: null,
    competencyEndDate: null,
    riferimentoAmministrazione: '',
    includePrices: null,
  };
};

const defaultCustomerStamp = (fiscalRegime: FiscalRegime | undefined | null): DocumentBuilderLine => ({
  code: '',
  articleCodes: [],
  description: 'Bollo in fattura',
  extendedDescription: '',
  discount: defaultDiscount,
  quantity: '1',
  unit: '',
  netPrice: '2.00',
  grossPrice: '2.00',
  vat: {
    lawReference: null,
    percentage: '0',
    taxExemption: fiscalRegime === FiscalRegime.RF19 ? 'N2.2' : 'N1',
  },
  managementData: [{ dataType: 'Bollo', text: 'Bollo in fattura', date: null, number: null }],
  competencyStartDate: null,
  competencyEndDate: null,
  riferimentoAmministrazione: '',
});

export const createDefaultItems = (
  vat: DocumentBuilderLineVAT,
  stamp: StampSettings | null,
  fiscalRegime?: FiscalRegime | null,
): DocumentBuilderLine[] => {
  const emptyItem = createDefaultItem(vat);

  if (stamp?.type === StampType.Customer) {
    return [emptyItem, defaultCustomerStamp(fiscalRegime)];
  } else {
    return [emptyItem];
  }
};

export const createDefaultStamp = (stamp: StampSettings | null) => {
  if (stamp === null) return null;
  return { type: stamp.type, value: '2.00' };
};

export const defaultTransportInformations: TransportInformations = {
  destination: { city: '', postalCode: '', address: '', country: 'IT' },
  transportReason: null,
  courier: null,
  packsNumber: '1',
  weight: '0',
};

export const createDefaultPaymentInfo = (payment: PaymentSettings) => {
  return {
    accountId: payment.accountId,
    institutionName: payment.institutionName,
    paymentAccount: payment.paymentAccount ?? '',
    paymentMethod: payment.paymentMethod,
  };
};

export const createDefaultForm = (
  fiscalRegime: undefined | null | FiscalRegime,
  { vat, stamp, payment, socialSecurity, withholdingTax }: InvoicingSettings,
  // Needed for retro compatibility, it is the local storage value saved in the atom
  useGrossPricesState = false,
): DocumentBuilderForm => {
  return {
    attachmentsSize: 0,
    categoryId: null,
    counterpart: null,
    currency: 'EUR',
    date: startOfDay(new Date()),
    fiscalRegime: fiscalRegime ?? FiscalRegime.RF01,
    flows: [createEmptyFlow(new Date(), payment)],
    includePrices: null,
    items: createDefaultItems({ ...vat, lawReference: null }, stamp, fiscalRegime),
    isEInvoice: true,
    motivation: null,
    notes: null,
    paymentInfo: createDefaultPaymentInfo(payment),
    subcategoryId: null,
    type: DocumentType.Invoice,
    subtype: defaultEInvoiceType(DocumentType.Invoice),
    relatedDocuments: [],
    vatCollection: null,
    withholdingTax: createDefaultWitholdingTax(withholdingTax),
    socialSecurity: createDefaultSocialSecurity(socialSecurity),
    riferimentoAmministrazione: null,
    stamp: createDefaultStamp(stamp),
    transportInformations: null,
    useGrossPrices: useGrossPricesState,
  };
};

function createDefaultSocialSecurity(socialSecurity: Omit<SocialSecurityForm, 'taxableAmount'> | null) {
  return socialSecurity ? { ...socialSecurity, taxableAmount: '0' } : null;
}

function createDefaultWitholdingTax(withholdingTax: Omit<WithholdingTaxForm, 'taxableAmount' | 'taxableMode'>[]) {
  return withholdingTax.map((w) => ({ ...w, taxableAmount: '0', taxableMode: TaxableMode.Percentage }));
}

export function fillValues(
  fiscalRegime: FiscalRegime | undefined | null,
  invoicingSettings: InvoicingSettings,
  values: Partial<DocumentBuilderForm>,
): DocumentBuilderForm {
  const { payment } = invoicingSettings;
  const emptyForm = createDefaultForm(fiscalRegime, invoicingSettings);

  return {
    categoryId: values.categoryId ?? emptyForm.categoryId,
    counterpart: values.counterpart ?? emptyForm.counterpart,
    currency: values.currency ?? emptyForm.currency,
    date: values.date ?? emptyForm.date,
    fiscalRegime: fiscalRegime ?? FiscalRegime.RF01,
    flows:
      values.flows && values.flows.length > 0
        ? values.flows
        : [createEmptyFlow(values.date ?? new Date(), payment, values.counterpart)],
    isEInvoice: values.isEInvoice ?? true,
    includePrices: values.includePrices ?? null,
    items: values.items && values.items.length > 0 ? values.items : emptyForm.items,
    motivation: values.motivation ?? emptyForm.motivation,
    notes: values.notes ?? emptyForm.notes,
    paymentInfo: values.paymentInfo ?? {
      accountId: null,
      institutionName: null,
      paymentMethod: PaymentMethod.Transfer,
      paymentAccount: '',
    },
    relatedDocuments: values.relatedDocuments ?? emptyForm.relatedDocuments,
    subcategoryId: values.subcategoryId ?? emptyForm.subcategoryId,
    type: values.type ?? emptyForm.type,
    subtype: values.subtype ?? defaultEInvoiceType(values.type ?? emptyForm.type),
    vatCollection: values.vatCollection ?? emptyForm.vatCollection,
    withholdingTax: values.withholdingTax ?? emptyForm.withholdingTax,
    socialSecurity: values.socialSecurity === null ? null : (values.socialSecurity ?? emptyForm.socialSecurity),
    riferimentoAmministrazione: values.riferimentoAmministrazione ?? null,
    stamp: values.stamp ?? emptyForm.stamp,
    transportInformations: values.transportInformations
      ? values.transportInformations
      : values.type === DocumentType.DeliveryNote
        ? defaultTransportInformations
        : emptyForm.transportInformations,
    useGrossPrices: values.useGrossPrices ?? emptyForm.useGrossPrices,
  };
}
